import React, { FC, ReactElement, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { createContext } from 'use-context-selector';

import { selectModal, setAnchor, setModalActive, setOpen } from '../store/slices/modalSlice';

export interface ModalType {
  children: ReactElement | string;
  anchor: string;
  open: boolean;
  modalActive?: Number;
  handleOpen: Function;
  handleAnchor: Function;
  handleChildren: Function;
  handleModalActive: Function;
}

export const ModalContext = createContext<ModalType>({
  children: '',
  anchor: 'right',
  open: false,
  modalActive: 0,
  handleOpen: () => {},
  handleAnchor: () => {},
  handleChildren: () => {},
  handleModalActive: () => {},
});

export const ModalProvider: FC<{ children: ReactElement }> = ({ children }) => {
  const dispatch = useDispatch();
  const { open, anchor, modalActive } = useSelector(selectModal);
  const [childrenValue, setChildrenValue] = useState<ReactElement | string>('');

  const handleOpen = useCallback(
    (value: boolean) => {
      dispatch(setOpen(value));
    },
    [dispatch],
  );

  const handleAnchor = useCallback(
    (value: string) => {
      dispatch(setAnchor(value));
    },
    [dispatch],
  );

  const handleChildren = useCallback((value: ReactElement) => {
    setChildrenValue(value);
  }, []);

  const handleModalActive = useCallback(
    (value: number) => {
      dispatch(setModalActive(value));
    },
    [dispatch],
  );

  const value = useMemo(
    () => ({
      anchor,
      children: childrenValue,
      open,
      modalActive,
      handleOpen,
      handleAnchor,
      handleChildren,
      handleModalActive,
    }),
    [
      anchor,
      childrenValue,
      open,
      modalActive,
      handleOpen,
      handleAnchor,
      handleChildren,
      handleModalActive,
    ],
  );

  return <ModalContext.Provider value={value}>{children}</ModalContext.Provider>;
};
